import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import type { WorkbookDefinition } from '@erp/shared-types';
import { sheetKindOf } from '@erp/workbooks';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import type { WorkbookAgentWorkspace } from './useWorkbookAgentTools';
import type { WorkbookSopUpdates } from './useWorkbookSopUpdates';
import { SHEET_KIND_UI } from './sheetKindUi';

/** Tabs that are not Items, as the agent names them. */
const PAGE_TABS = [
  'process',
  'runs',
  'automations',
  'versions',
  'settings',
] as const;

function itemsOf(definition: WorkbookDefinition) {
  return [
    ...definition.files.map((item) => ({ ...item, kind: 'file' })),
    ...definition.sources.map((item) => ({ ...item, kind: 'source' })),
    ...definition.sheets.map((item) => ({
      ...item,
      kind: SHEET_KIND_UI[sheetKindOf(item)].agentLabel,
    })),
    ...definition.outputs.map((item) => ({
      ...item,
      kind: `${item.kind} output`,
    })),
  ];
}

/** A tab as the agent reads it: its id, and for an Item its name and kind. */
function describeTab(definition: WorkbookDefinition, id: string) {
  const item = itemsOf(definition).find((entry) => entry.id === id);
  return item ? { id, name: item.name, kind: item.kind } : { id };
}

function sopUpdatesRead(
  definition: WorkbookDefinition,
  sopUpdates: WorkbookSopUpdates,
) {
  const titles = new Map(
    definition.processSteps.map((step) => [step.id, step.title ?? null]),
  );
  return {
    automatic: definition.sopAutoApply === true,
    staleInstructions: sopUpdates.candidates.map((stepId) => ({
      stepId,
      title: titles.get(stepId) ?? null,
      proposal: sopUpdates.proposals.has(stepId),
    })),
    proposals: [...sopUpdates.proposals.values()].map((update) => ({
      stepId: update.stepId,
      proposedTitle: update.proposedTitle,
      proposedInstructions: update.proposedInstructions,
      summary: update.summary,
    })),
    unavailable: [...sopUpdates.unavailable.values()].map((update) => ({
      stepId: update.stepId,
      reason: update.reason,
    })),
    regenerating: [...sopUpdates.regenerating],
  };
}

/**
 * The agent tools that act on the Workbook page rather than its Definition:
 * which tab the user is on, opening one, and SOP updates. Hands them, with
 * the tab shown, to the editor's agent registration through `register`.
 */
export function useWorkbookWorkspaceAgentTools({
  definition,
  selected,
  opened,
  openItem,
  sopUpdates,
  canUpdate,
  register,
}: {
  definition: WorkbookDefinition;
  selected: string;
  opened: readonly string[];
  openItem: (id: string) => void;
  sopUpdates: WorkbookSopUpdates;
  canUpdate: boolean;
  register: (workspace: WorkbookAgentWorkspace) => void;
}) {
  const viewTool = useFrontendTool({
    name: 'get_workbook_view',
    description:
      'Read what the user is looking at: the tab shown and the tabs open, each an item (with its name and kind) or a page tab (process, runs, automations, versions, settings).',
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: () => ({
      selected: describeTab(definition, selected),
      opened: opened.map((id) => describeTab(definition, id)),
    }),
  });
  const openTool = useFrontendTool({
    name: 'open_workbook_item',
    description:
      'Show the user a tab: a file, source, worksheet or output by its id, or a page tab (process, runs, automations, versions, settings). Use it to point at what you changed or are talking about.',
    parameters: z.object({ id: z.string().min(1) }),
    readOnlyHint: true,
    awaitable: true,
    execute: (input) => {
      const known =
        (PAGE_TABS as readonly string[]).includes(input.id) ||
        itemsOf(definition).some((item) => item.id === input.id);
      if (!known)
        return { ok: false, error: `No item or tab has the id ${input.id}.` };
      openItem(input.id);
      return describeTab(definition, input.id);
    },
  });
  const sopTool = useFrontendTool({
    name: 'workbook_sop_updates',
    description:
      "Read the SOP updates: instructions whose settings changed since their text was written (stale), each one's proposed rewrite when the review has written one, and reviews that reached no verdict. automatic means proposals apply as they arrive.",
    parameters: z.object({}),
    readOnlyHint: true,
    awaitable: true,
    execute: () => sopUpdatesRead(definition, sopUpdates),
  });
  const regenerateTool = useFrontendTool({
    name: 'regenerate_instructions',
    enabled: canUpdate,
    description:
      "Ask for SOP rewrites of these instructions from their current settings, as the user's Regenerate does. The rewrites open in a preview for the user to accept (or apply at once when automatic); read workbook_sop_updates for them. To write an instruction's text yourself, use edit_workbook instead.",
    parameters: z.object({
      stepIds: z.array(z.string().min(1)).min(1).max(50),
    }),
    awaitable: true,
    execute: async (input) => {
      const unknown = input.stepIds.filter(
        (id) => !definition.processSteps.some((step) => step.id === id),
      );
      if (unknown.length)
        return {
          ok: false,
          error: `No instruction has the id ${unknown.join(', ')}.`,
        };
      await sopUpdates.regenerate(input.stepIds);
      return { requested: input.stepIds };
    },
  });
  const resolveTool = useFrontendTool({
    name: 'resolve_sop_update',
    enabled: canUpdate,
    description:
      "Accept (apply the proposed rewrite to the instruction, one step the user can undo) or discard an instruction's pending SOP update. Only when the user asks.",
    parameters: z.object({
      stepId: z.string().min(1),
      resolution: z.enum(['accept', 'discard']),
    }),
    awaitable: true,
    execute: (input) => {
      const update = sopUpdates.proposals.get(input.stepId);
      if (!update)
        return {
          ok: false,
          error:
            'This instruction has no pending SOP update. Read workbook_sop_updates.',
        };
      if (input.resolution === 'accept') sopUpdates.accept([update]);
      else sopUpdates.discard(update);
      return { stepId: input.stepId, resolution: input.resolution };
    },
  });
  const tools = useMemo(
    () => [viewTool, openTool, sopTool, regenerateTool, resolveTool],
    [viewTool, openTool, sopTool, regenerateTool, resolveTool],
  );
  const openedKey = opened.join(',');
  useEffect(() => {
    register({
      selected,
      opened: openedKey ? openedKey.split(',') : [],
      tools,
    });
  }, [register, selected, openedKey, tools]);
}
